import { z } from 'zod';
import { BuildingIdSchema, OperationIdSchema } from '../../ids';
import {
  InventorySchema,
  ObjectIdSchema,
  StorageViewSchema,
  TransferDirectionSchema,
} from '../../models/inventory';
import { ItemIdSchema, PositiveQuantitySchema } from '../../models/items';
import { API_PREFIX, defineEndpoint } from '../endpoint';

/** The player's backpack, the main storage of their own fixed lot and the other storages they hold. */
export const getInventory = defineEndpoint({
  method: 'GET',
  path: `${API_PREFIX}/me/inventory`,
  access: 'session',
  response: InventorySchema,
});

/**
 * Moves items between the backpack and a storage: the main one of the player's own fixed lot (the
 * character on it, next to it; PrimeraVersion.md §5; C9) or, named by `storage`, another one
 * the player holds, standing within its reach (multiple storages).
 *
 * Idempotent: retrying with the same `operationId` and the same body returns the original
 * result without moving anything again; reusing it with a different body is a conflict.
 */
export const transferItems = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/me/inventory/transfers`,
  access: 'session',
  body: z.object({
    operationId: OperationIdSchema,
    direction: TransferDirectionSchema,
    item: ItemIdSchema,
    quantity: PositiveQuantitySchema,
    /** The storage's building; absent: the main storage. */
    storage: BuildingIdSchema.optional(),
  }),
  response: z.object({ operationId: OperationIdSchema, inventory: InventorySchema }),
});

/**
 * Moves one unique object (EQUIP-SLOT) between the backpack and a storage (the main one, or the
 * one `storage` names), next to it, like goods. Idempotent by operation id.
 */
export const transferObject = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/me/inventory/objects`,
  access: 'session',
  body: z.object({
    operationId: OperationIdSchema,
    direction: TransferDirectionSchema,
    object: ObjectIdSchema,
    /** The storage's building; absent: the main storage. */
    storage: BuildingIdSchema.optional(),
  }),
  response: z.object({ operationId: OperationIdSchema, inventory: InventorySchema }),
});

/**
 * Reads an item the character carries (ITEM-READ, IR-1..3): its words are the client's to show;
 * a text written to teach places (a quest's thing, a document) makes them known here. Idempotent.
 */
export const readItem = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/me/read`,
  access: 'session',
  body: z.object({ operationId: OperationIdSchema, item: ItemIdSchema }),
  response: z.object({
    operationId: OperationIdSchema,
    /** The places its reading made known that were not (none: nothing new). */
    places: z.array(z.string()),
  }),
});

/**
 * What a storage holds, read standing by it: the own ones (every one is in the inventory too) and
 * someone else's while it stands open to whoever looks (plan C6: forced, a ruin's or a piece in
 * ruin, SIEGE H2a), to take from. `locked` while it holds, `permission_denied` for what is never
 * opened (a plot's).
 */
export const getStorageContents = defineEndpoint({
  method: 'GET',
  path: `${API_PREFIX}/buildings/:buildingId/contents`,
  access: 'session',
  params: z.object({ buildingId: BuildingIdSchema }),
  response: StorageViewSchema,
});
